import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Users, UserPlus, Clock, CheckCircle, XCircle, Copy, Trash2, LogOut, 
  ArrowLeft, Search, Edit, User, Filter, Download,
  X, Calendar, SortAsc, SortDesc, UserCheck, Ban
} from 'lucide-react';
import Header from './Header';
import UserRoleEditor from './UserRoleEditor';
import UserSessionViewer from './UserSessionViewer';
import InvitationActions from './InvitationActions';
import { getAllUsers, getAllInvitations, forceUserLogout } from '../lib/userService';
import { matchesSearchTerm } from '../lib/searchUtils';
import { 
  getAllActiveSessions, 
  forceLogoutSession, 
  subscribeToAllSessions, 
  getUserActiveSessions 
} from '../lib/sessionService';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingInvitations: 0,
    usersByRole: { admin: 0, shopkeeper: 0, solo_lectura: 0 }
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'solo_lectura' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [showSessionViewer, setShowSessionViewer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { isAdmin, currentUser } = useAuth();

  // Load active sessions data
  const fetchSessions = async () => {
    try {
      setLoading(true);
      const allSessions = await getAllActiveSessions();
      setSessions(allSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Error al cargar las sesiones activas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) {
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'invitations') {
        fetchInvitations();
      // Session tracking disabled
      // } else if (activeTab === 'sessions') {
      //   fetchSessions();
      } else if (activeTab === 'stats') {
        fetchUserStats();
      }
    }
  }, [activeTab, currentUser, isAdmin]);

  // Subscribe to real-time session updates
  useEffect(() => {
    if (!isAdmin() || activeTab !== 'sessions') return;

    const unsubscribe = subscribeToAllSessions((updatedSessions) => {
      setSessions(updatedSessions);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [activeTab, isAdmin]);

  // Session tracking disabled - auto-refresh removed
  // useEffect(() => {
  //   if (activeTab === 'sessions' && isAdmin()) {
  //     const interval = setInterval(fetchSessions, 30000);
  //     return () => clearInterval(interval);
  //   }
  // }, [activeTab, isAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simple query without composite index requirement
      // We'll filter and sort in JavaScript (local-first approach)
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      let usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter out invited users and sort in JavaScript
      usersList = usersList
        .filter(user => user.status !== 'invited') // Exclude invited users
        .sort((a, b) => {
          // Sort by status first (active users first), then by creation date (newest first)
          if (a.status !== b.status) {
            if (a.status === 'active') return -1;
            if (b.status === 'active') return 1;
            return 0;
          }
          
          // Sort by creation date (newest first)
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const q = query(collection(db, 'invitations'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const invitationsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvitations(invitationsList);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Error al cargar las invitaciones');
    } finally {
      setLoading(false);
    }
  };


  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError('');
      
      const [users, invitations] = await Promise.all([
        getAllUsers(),
        getAllInvitations()
      ]);
      
      const activeUsers = users.filter(user => {
        if (!user.lastLogin) return false;
        const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
        const daysSinceLogin = (new Date() - lastLogin) / (1000 * 60 * 60 * 24);
        return daysSinceLogin <= 30; // Active in last 30 days
      }).length;
      
      const pendingInvitations = invitations.filter(inv => {
        if (inv.status !== 'pending') return false;
        const expiresAt = inv.expiresAt.toDate ? inv.expiresAt.toDate() : new Date(inv.expiresAt);
        return new Date() < expiresAt;
      }).length;
      
      const usersByRole = users.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, { admin: 0, shopkeeper: 0, solo_lectura: 0 });
      
      setUserStats({
        totalUsers: users.length,
        activeUsers,
        pendingInvitations,
        usersByRole
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setStatsError('Error al cargar estadísticas de usuarios');
    } finally {
      setStatsLoading(false);
    }
  };

  const createInvitation = async (e) => {
    e.preventDefault();
    if (!newInvitation.email || !newInvitation.role) {
      setError('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if invitation already exists for this email
      const q = query(collection(db, 'invitations'), where('email', '==', newInvitation.email));
      const existingInvitations = await getDocs(q);
      
      if (!existingInvitations.empty) {
        setError('Ya existe una invitación para este email');
        setLoading(false);
        return;
      }

      // Generate invitation token
      const invitationToken = generateInvitationToken();
      
      // Create invitation document
      await addDoc(collection(db, 'invitations'), {
        email: newInvitation.email,
        role: newInvitation.role,
        token: invitationToken,
        status: 'pending',
        createdAt: new Date(),
        createdBy: currentUser.uid,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        usedAt: null
      });

      setSuccess('Invitación creada exitosamente');
      setNewInvitation({ email: '', role: 'solo_lectura' });
      fetchInvitations();
    } catch (error) {
      console.error('Error creating invitation:', error);
      setError('Error al crear la invitación');
    } finally {
      setLoading(false);
    }
  };

  const generateInvitationToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const copyInvitationLink = (token) => {
    const invitationLink = `${window.location.origin}/register?invitation=${token}`;
    navigator.clipboard.writeText(invitationLink);
    setSuccess('Enlace copiado al portapapeles');
  };

  const deleteInvitation = async (invitationId) => {
    if (!window.confirm('¿Está seguro de eliminar esta invitación?')) return;

    try {
      await deleteDoc(doc(db, 'invitations', invitationId));
      setSuccess('Invitación eliminada');
      fetchInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError('Error al eliminar la invitación');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    if (!window.confirm('¿Está seguro de cambiar el rol de este usuario?')) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date()
      });
      setSuccess('Rol actualizado exitosamente');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Error al actualizar el rol');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activar' : 'desactivar';
    
    if (!window.confirm(`¿Está seguro de ${action} este usuario?`)) return;

    try {
      await updateDoc(doc(db, 'users', userId), {
        status: newStatus,
        updatedAt: new Date()
      });
      setSuccess(`Usuario ${action === 'activar' ? 'activado' : 'desactivado'} exitosamente`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Error al actualizar el estado del usuario');
    }
  };

  const forceLogout = async (sessionId, isCurrentSession) => {
    if (isCurrentSession) {
      setError('No puedes cerrar tu propia sesión desde aquí');
      return;
    }

    if (!window.confirm('¿Está seguro de cerrar esta sesión?')) return;

    try {
      // Force logout session using sessionService
      await forceLogoutSession(sessionId, currentUser?.uid);
      
      // Remove session from local state
      setSessions(sessions.filter(session => session.id !== sessionId));
      
      setSuccess('Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error forcing logout:', error);
      setError('Error al cerrar la sesión');
    }
  };

  // Modal handlers
  const handleOpenRoleEditor = (user) => {
    setSelectedUser(user);
    setShowRoleEditor(true);
  };

  const handleCloseRoleEditor = () => {
    setShowRoleEditor(false);
    setSelectedUser(null);
  };

  const handleRoleChange = (userId, newRole, newStatus) => {
    // Refresh users data
    fetchUsers();
    setSuccess(`Usuario actualizado exitosamente`);
  };

  const handleOpenSessionViewer = (user) => {
    setSelectedUser(user);
    setShowSessionViewer(true);
  };

  const handleCloseSessionViewer = () => {
    setShowSessionViewer(false);
    setSelectedUser(null);
  };

  const handleSessionLogout = async (sessionId, userId) => {
    try {
      // Force logout session using sessionService
      await forceLogoutSession(sessionId, currentUser?.uid);
      
      // Filter out the session from local state
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error forcing logout session:', error);
      throw error;
    }
  };

  // Invitation handlers
  const handleInvitationCopy = (token, link) => {
    setSuccess('Enlace de invitación copiado al portapapeles');
  };

  const handleInvitationDelete = (invitationId) => {
    fetchInvitations();
    setSuccess('Invitación eliminada exitosamente');
  };

  const handleInvitationResend = (email, newToken) => {
    fetchInvitations();
    setSuccess(`Nueva invitación enviada a ${email}`);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'used':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'used':
        return 'Usado';
      case 'expired':
        return 'Expirado';
      default:
        return 'Desconocido';
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'shopkeeper':
        return 'Shopkeeper';
      case 'solo_lectura':
        return 'Solo Lectura';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400';
      case 'shopkeeper':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400';
      case 'solo_lectura':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const isExpired = (expiresAt) => {
    return new Date() > expiresAt.toDate();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
    return dateObj.toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSessionDuration = (loginTime) => {
    const now = new Date();
    const login = loginTime instanceof Date ? loginTime : (loginTime.toDate ? loginTime.toDate() : new Date(loginTime));
    const diffMs = now - login;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  // Helper functions for session data
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.displayName || 'Usuario';
  };

  const getUserEmail = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.email || 'Sin email';
  };

  const getDeviceIcon = (device) => {
    if (!device) return <Monitor className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
    
    const deviceLower = device.toLowerCase();
    if (deviceLower.includes('mobile') || deviceLower.includes('android') || deviceLower.includes('iphone')) {
      return <Monitor className="h-4 w-4 text-gray-400 dark:text-gray-500" />; // Using Monitor for now, could be Smartphone
    }
    return <Monitor className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
  };

  // Enhanced filtering and sorting
  const applyFilters = (items, type) => {
    let filtered = items;
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const searchFields = type === 'users' 
          ? [item.email, item.displayName, getRoleText(item.role)]
          : [item.email, getRoleText(item.role)];
        
        return searchFields.some(field => 
          field && matchesSearchTerm(field, searchTerm)
        );
      });
    }
    
    // Role filter
    if (filters.role) {
      filtered = filtered.filter(item => item.role === filters.role);
    }
    
    // Status filter (only for users)
    if (filters.status && type === 'users') {
      filtered = filtered.filter(item => item.status === filters.status);
    }
    
    // Date range filter
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter(item => {
        const itemDate = item.createdAt?.toDate ? item.createdAt.toDate() : new Date(item.createdAt);
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
        
        if (fromDate && itemDate < fromDate) return false;
        if (toDate && itemDate > toDate) return false;
        return true;
      });
    }
    
    return filtered;
  };
  
  const sortItems = (items, type) => {
    return [...items].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'name':
          aValue = type === 'users' ? (a.displayName || a.email) : a.email;
          bValue = type === 'users' ? (b.displayName || b.email) : b.email;
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'createdAt':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          bValue = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          break;
        case 'lastLogin':
          aValue = a.lastLogin?.toDate ? a.lastLogin.toDate() : new Date(0);
          bValue = b.lastLogin?.toDate ? b.lastLogin.toDate() : new Date(0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  const filteredUsers = sortItems(applyFilters(users, 'users'), 'users');
  const filteredInvitations = sortItems(applyFilters(invitations, 'invitations'), 'invitations');
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const clearFilters = () => {
    setFilters({ role: '', status: '', dateFrom: '', dateTo: '' });
    setSearchTerm('');
    setSortConfig({ key: 'createdAt', direction: 'desc' });
  };
  
  const exportToCSV = () => {
    const data = activeTab === 'users' ? filteredUsers : filteredInvitations;
    const headers = activeTab === 'users' 
      ? ['Nombre', 'Email', 'Rol', 'Estado', 'Fecha Registro', 'Último Acceso']
      : ['Email', 'Rol', 'Estado', 'Fecha Creación', 'Fecha Expiración'];
    
    const csvContent = [
      headers.join(','),
      ...data.map(item => {
        if (activeTab === 'users') {
          return [
            item.displayName || 'Sin nombre',
            item.email,
            getRoleText(item.role),
            item.status,
            formatDate(item.createdAt),
            formatDateTime(item.lastLogin)
          ].join(',');
        } else {
          return [
            item.email,
            getRoleText(item.role),
            getStatusText(item.status),
            formatDate(item.createdAt),
            formatDate(item.expiresAt)
          ].join(',');
        }
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab === 'users' ? 'usuarios' : 'invitaciones'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            No tienes permisos para acceder a esta sección
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="mb-4 sm:mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm sm:text-base">Volver al Dashboard</span>
          </Link>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <User className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 dark:text-red-400 flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white dark:text-white">Usuarios</h2>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex px-4 sm:px-6 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'users'
                    ? 'border-red-500 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Users className="h-8 w-8 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Usuarios Registrados</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'invitations'
                    ? 'border-red-500 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <UserPlus className="h-8 w-8 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Invitaciones</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'stats'
                    ? 'border-red-500 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <User className="h-8 w-8 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Estadísticas</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mx-6 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
              {success}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'users' && (
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white dark:text-white">Usuarios Registrados</h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="inline-flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 text-sm"
                    >
                      <Filter className="h-4 w-4" />
                      <span className="hidden sm:inline">Filtros</span>
                    </button>
                    <button
                      onClick={exportToCSV}
                      className="inline-flex items-center justify-center space-x-2 px-3 py-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md hover:bg-green-200 dark:hover:bg-green-900/30 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      <span className="hidden sm:inline">Exportar CSV</span>
                    </button>
                  </div>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full sm:w-48 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {/* Advanced Filters */}
              {showFilters && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
                      <select
                        value={filters.role}
                        onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white dark:text-white"
                      >
                        <option value="">Todos</option>
                        <option value="admin">Administrador</option>
                        <option value="shopkeeper">Shopkeeper</option>
                        <option value="solo_lectura">Solo Lectura</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white dark:text-white"
                      >
                        <option value="">Todos</option>
                        <option value="active">Activo</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desde</label>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hasta</label>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center space-x-2">
                    <button
                      onClick={clearFilters}
                      className="inline-flex items-center space-x-2 px-3 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                    >
                      <X className="h-4 w-4" />
                      <span>Limpiar Filtros</span>
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">
                      Mostrando {filteredUsers.length} de {users.length} usuarios
                    </span>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 dark:text-gray-400">
                  Cargando usuarios...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 dark:text-gray-400">
                  No hay usuarios registrados
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('name')}
                            className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <span>USUARIO</span>
                            {sortConfig.key === 'name' && (
                              sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                            )}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          <button
                            onClick={() => handleSort('role')}
                            className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <span>ROL</span>
                            {sortConfig.key === 'role' && (
                              sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                            )}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          <button
                            onClick={() => handleSort('createdAt')}
                            className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <span>FECHA REGISTRO</span>
                            {sortConfig.key === 'createdAt' && (
                              sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                            )}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          <button
                            onClick={() => handleSort('lastLogin')}
                            className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <span>ÚLTIMO ACCESO</span>
                            {sortConfig.key === 'lastLogin' && (
                              sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                            )}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                          ESTADO
                        </th>
                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                          ACCIONES
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            <div className="min-w-0">
                              <div className="font-semibold truncate">
                                {user.displayName || 'Sin nombre'}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                              {/* Mobile info */}
                              <div className="md:hidden mt-2 space-y-1">
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Rol:</span>{' '}
                                  <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                                    {getRoleText(user.role)}
                                  </span>
                                </div>
                                <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Estado:</span>{' '}
                                  <span className={`inline-flex items-center space-x-1 ${
                                    user.status === 'active' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    <div className={`h-1.5 w-1.5 rounded-full ${
                                      user.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                                    }`}></div>
                                    <span className="capitalize">{user.status}</span>
                                  </span>
                                </div>
                                <div className="lg:hidden text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Registro:</span> {formatDate(user.createdAt)}
                                </div>
                                <div className="lg:hidden text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Último acceso:</span> {formatDateTime(user.lastLogin)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap hidden md:table-cell">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                              {getRoleText(user.role)}
                            </span>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                            {formatDateTime(user.lastLogin)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap hidden sm:table-cell">
                            <div className="flex items-center space-x-2">
                              <div className={`h-2 w-2 rounded-full ${
                                user.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              <span className="text-sm text-gray-900 dark:text-white capitalize">{user.status}</span>
                            </div>
                          </td>
                          <td className="px-2 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleOpenRoleEditor(user)}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Editar Rol"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => toggleUserStatus(user.id, user.status)}
                                className={`p-1 rounded ${
                                  user.status === 'active'
                                    ? 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                    : 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                                }`}
                                title={user.status === 'active' ? 'Desactivar' : 'Activar'}
                              >
                                {user.status === 'active' ? (
                                  <Ban className="h-4 w-4" />
                                ) : (
                                  <UserCheck className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invitations' && (
            <div>
              {/* Create Invitation Form */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Crear Nueva Invitación</h3>
                
                <form onSubmit={createInvitation} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email del Usuario
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={newInvitation.email}
                        onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="usuario@ejemplo.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Rol
                      </label>
                      <select
                        id="role"
                        value={newInvitation.role}
                        onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="solo_lectura">Solo Lectura</option>
                        <option value="shopkeeper">Shopkeeper</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>{loading ? 'Creando...' : 'Crear Invitación'}</span>
                  </button>
                </form>
              </div>

              {/* Invitations List */}
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Invitaciones Pendientes</h3>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="inline-flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 text-sm"
                      >
                        <Filter className="h-4 w-4" />
                        <span className="hidden sm:inline">Filtros</span>
                      </button>
                      <button
                        onClick={exportToCSV}
                        className="inline-flex items-center justify-center space-x-2 px-3 py-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md hover:bg-green-200 dark:hover:bg-green-900/30 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 text-sm"
                      >
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">Exportar CSV</span>
                      </button>
                    </div>
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:w-48 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Advanced Filters for Invitations */}
                {showFilters && (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
                        <select
                          value={filters.role}
                          onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                        >
                          <option value="">Todos</option>
                          <option value="admin">Administrador</option>
                          <option value="shopkeeper">Shopkeeper</option>
                          <option value="solo_lectura">Solo Lectura</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desde</label>
                        <input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hasta</label>
                        <input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center space-x-2">
                      <button
                        onClick={clearFilters}
                        className="inline-flex items-center space-x-2 px-3 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                      >
                        <X className="h-4 w-4" />
                        <span>Limpiar Filtros</span>
                      </button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Mostrando {filteredInvitations.length} de {invitations.length} invitaciones
                      </span>
                    </div>
                  </div>
                )}
                
                {loading ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Cargando invitaciones...
                  </div>
                ) : filteredInvitations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No hay invitaciones creadas
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                            <button
                              onClick={() => handleSort('email')}
                              className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              <span>EMAIL</span>
                              {sortConfig.key === 'email' && (
                                sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                            <button
                              onClick={() => handleSort('role')}
                              className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              <span>ROL</span>
                              {sortConfig.key === 'role' && (
                                sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                            ESTADO
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                            <button
                              onClick={() => handleSort('createdAt')}
                              className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              <span>CREADA</span>
                              {sortConfig.key === 'createdAt' && (
                                sortConfig.direction === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                            EXPIRA
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                            ACCIONES
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredInvitations.map((invitation) => {
                          const expired = isExpired(invitation.expiresAt);
                          const actualStatus = expired && invitation.status === 'pending' ? 'expired' : invitation.status;
                          
                          return (
                            <tr key={invitation.id}>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-white">
                                <div className="min-w-0">
                                  <div className="font-semibold truncate">
                                    {invitation.email}
                                  </div>
                                  {/* Mobile info */}
                                  <div className="md:hidden mt-2 space-y-1">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      <span className="font-medium">Rol:</span>{' '}
                                      <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getRoleBadgeColor(invitation.role)}`}>
                                        {getRoleText(invitation.role)}
                                      </span>
                                    </div>
                                    <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400">
                                      <span className="font-medium">Estado:</span>{' '}
                                      <span className="inline-flex items-center space-x-1">
                                        {getStatusIcon(actualStatus)}
                                        <span>{getStatusText(actualStatus)}</span>
                                      </span>
                                    </div>
                                    <div className="lg:hidden text-xs text-gray-500 dark:text-gray-400">
                                      <span className="font-medium">Creada:</span> {formatDate(invitation.createdAt)}
                                    </div>
                                    <div className="lg:hidden text-xs text-gray-500 dark:text-gray-400">
                                      <span className="font-medium">Expira:</span> {formatDate(invitation.expiresAt)}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap hidden md:table-cell">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(invitation.role)}`}>
                                  {getRoleText(invitation.role)}
                                </span>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap hidden sm:table-cell">
                                <div className="flex items-center space-x-2">
                                  {getStatusIcon(actualStatus)}
                                  <span className="text-sm text-gray-900 dark:text-white">{getStatusText(actualStatus)}</span>
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                                {formatDate(invitation.createdAt)}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                                {formatDate(invitation.expiresAt)}
                              </td>
                              <td className="px-2 py-4 whitespace-nowrap text-sm font-medium">
                                <InvitationActions
                                  invitation={invitation}
                                  onCopy={handleInvitationCopy}
                                  onDelete={handleInvitationDelete}
                                  onResend={handleInvitationResend}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session tracking disabled - sessions tab removed */}
          {false && (
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sesiones Activas</h3>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Actualización automática cada 30 segundos
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Cargando sesiones...
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No hay sesiones activas
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                          Usuario
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          Inicio
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                          Actividad
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          Dispositivo
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden xl:table-cell">
                          IP
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          Duración
                        </th>
                        <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-20">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {sessions.map((session) => (
                        <tr 
                          key={session.id}
                          className={session.isCurrentSession ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                        >
                          <td className="px-2 sm:px-3 py-3 sm:py-4">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {getUserName(session.userId)}
                                {session.isCurrentSession && (
                                  <span className="ml-1 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                                    Tu sesión
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{getUserEmail(session.userId)}</div>
                              {/* Show additional info on mobile */}
                              <div className="sm:hidden mt-1 space-y-1">
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Actividad: {formatDateTime(session.lastActivity)}
                                </div>
                                <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 lg:hidden">
                                  {getDeviceIcon(session.device)}
                                  <span className="truncate">{session.device}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm text-gray-500 dark:text-gray-400 hidden md:table-cell">
                            <span className="whitespace-nowrap">{formatDateTime(session.loginTime)}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                            <span className="whitespace-nowrap">{formatDateTime(session.lastActivity)}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                            <div className="flex items-center space-x-1 min-w-0">
                              {getDeviceIcon(session.device)}
                              <span className="truncate">{session.device}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm text-gray-500 dark:text-gray-400 hidden xl:table-cell">
                            <span className="whitespace-nowrap">{session.ipAddress}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                            <span className="whitespace-nowrap">{getSessionDuration(session.loginTime)}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-sm font-medium">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => {
                                  const user = users.find(u => u.id === session.userId);
                                  if (user) handleOpenSessionViewer(user);
                                }}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Ver sesiones"
                              >
                                <Activity className="h-4 w-4" />
                              </button>
                              {!session.isCurrentSession && (
                                <button
                                  onClick={() => forceLogout(session.id, session.isCurrentSession)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                  title="Cerrar sesión"
                                >
                                  <LogOut className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Estadísticas de Usuarios</h3>
              
              {statsError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
                  {statsError}
                </div>
              )}
              
              {statsLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Cargando estadísticas...
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Usuarios</p>
                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{userStats.totalUsers}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <UserCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-green-600 dark:text-green-400">Usuarios Activos</p>
                        <p className="text-2xl font-bold text-green-900 dark:text-green-300">{userStats.activeUsers}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <UserPlus className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                      <div>
                        <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Invitaciones Pendientes</p>
                        <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-300">{userStats.pendingInvitations}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <User className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                      <div>
                        <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Administradores</p>
                        <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">{userStats.usersByRole.admin}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Usuarios por Rol</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">Administradores</span>
                    <span className="text-lg font-bold text-red-900 dark:text-red-300">{userStats.usersByRole.admin}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Shopkeepers</span>
                    <span className="text-lg font-bold text-blue-900 dark:text-blue-300">{userStats.usersByRole.shopkeeper}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Solo Lectura</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{userStats.usersByRole.solo_lectura}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Components */}
      <UserRoleEditor
        user={selectedUser}
        isOpen={showRoleEditor}
        onClose={handleCloseRoleEditor}
        onRoleChange={handleRoleChange}
      />
      
      <UserSessionViewer
        user={selectedUser}
        sessions={sessions}
        isOpen={showSessionViewer}
        onClose={handleCloseSessionViewer}
        onForceLogout={handleSessionLogout}
      />
    </div>
  );
};

export default UserManagement;